import React, { useState } from 'react';
import { color, weight } from '../theme';
import { getInitials } from '../utils/names';

interface AvatarProps {
  name: string;
  src?: string;
  size?: number;
  // White ring, for an avatar that overlaps a colored header band
  ring?: boolean;
  // People: 'circle' (face only), 'square' (a rounded square that keeps head and shoulders),
  // 'portrait' (3:4, `size` wide, the whole official photo). Logos: 'rounded' (fitted, not cropped).
  shape?: 'circle' | 'square' | 'portrait' | 'rounded';
  // A green dot in the corner: in the Knesset building now. Lists that show presence pass a
  // boolean (true or false), so the element stays the same when it changes.
  present?: boolean;
}

/* A photo that loads lazily at its display size, falling back to initials when there
   is no photo or it fails to load. Decorative: the name is always shown next to it.
   MK photos are 2:3 portraits, so the cropped shapes anchor near the top, where the face is. */
const RADIUS: Record<NonNullable<AvatarProps['shape']>, number> = { circle: 0.5, square: 0.2, portrait: 0.11, rounded: 0.25 };
const POSITION: Record<NonNullable<AvatarProps['shape']>, string> = { circle: 'center', square: 'center 15%', portrait: 'center top', rounded: 'center' };
const Avatar = ({ present, ...props }: AvatarProps) => {
  if (present === undefined) return <AvatarImage {...props} />;
  const { size = 40, shape = 'circle' } = props;
  const height = shape === 'portrait' ? Math.round(size * 4 / 3) : size;
  // The dot sits on the corner of the rounded shape, inside the avatar's box
  const dot = Math.max(10, Math.min(18, Math.round(size * 0.26)));
  const cornerRadius = shape === 'circle' ? size / 2 : size * RADIUS[shape];
  const inset = Math.max(0, Math.round(cornerRadius * (1 - Math.SQRT1_2) - dot / 2));
  return (
    <span style={{ position: 'relative', display: 'block', width: size, height, flexShrink: 0 }}>
      <AvatarImage {...props} />
      {present && (
        <span role="img" aria-label="במשכן עכשיו" title="במשכן עכשיו" style={{
          position: 'absolute', bottom: inset, left: inset, width: dot, height: dot, borderRadius: '50%',
          background: color.present, boxShadow: '0 0 0 2px #ffffff',
        }} />
      )}
    </span>
  );
};

const AvatarImage = ({ name, src, size = 40, ring = false, shape = 'circle' }: Omit<AvatarProps, 'present'>) => {
  const [failed, setFailed] = useState(false);
  const height = shape === 'portrait' ? Math.round(size * 4 / 3) : size;
  const box: React.CSSProperties = {
    width: size,
    height,
    flexShrink: 0,
    borderRadius: shape === 'circle' ? '50%' : Math.round(size * RADIUS[shape]),
    border: ring ? '3px solid #ffffff' : undefined,
    boxSizing: 'border-box',
    overflow: 'hidden',
    background: '#dfe5ec',
  };

  if (!src || failed) {
    return (
      <span aria-hidden="true" style={{
        ...box,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: color.textSecondary,
        fontSize: Math.max(12, Math.round(size * 0.36)),
        fontWeight: weight.bold,
      }}>
        {getInitials(name)}
      </span>
    );
  }
  return (
    <img
      src={src}
      alt=""
      width={size}
      height={height}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
      style={{ ...box, display: 'block', objectFit: shape === 'rounded' ? 'contain' : 'cover', objectPosition: POSITION[shape] }}
    />
  );
};

export default Avatar;
